import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const SurfaceContext = createContext(null)
const surfaces = new Set()
let lastDocument = null
export const isSurfaceOpen = doc => doc === document || (surfaces.has(doc) && !doc.defaultView?.closed)
export const SurfaceProvider = SurfaceContext.Provider
export const useSurfaceDocument = () => useContext(SurfaceContext) || document
export const getInteractionDocument = () => {
  try { if (lastDocument?.defaultView && !lastDocument.defaultView.closed) return lastDocument } catch {}
  return document
}
export function registerSurface (doc) {
  const activate = () => { lastDocument = doc }
  doc.addEventListener('pointerdown', activate, true)
  doc.addEventListener('focusin', activate, true)
  doc.addEventListener('keydown', activate, true)
  surfaces.add(doc)
  return () => {
    doc.removeEventListener('pointerdown', activate, true)
    doc.removeEventListener('focusin', activate, true)
    doc.removeEventListener('keydown', activate, true)
    surfaces.delete(doc)
    if (lastDocument === doc) lastDocument = document
    window.dispatchEvent(new Event('cc:surface-closed'))
  }
}
export function useSurfaceVisible (enabled = true) {
  const doc = useSurfaceDocument()
  const [visible, setVisible] = useState(!doc.hidden)
  useEffect(() => {
    const sync = () => setVisible(!doc.hidden)
    sync()
    doc.addEventListener('visibilitychange', sync)
    return () => doc.removeEventListener('visibilitychange', sync)
  }, [doc])
  return enabled && visible
}

// Global dialogs are created by the admin controller. Capture the window that
// launched them once; moving focus later must not move an open dialog.
export function WindowOverlay ({ children, ownerDocument }) {
  const initial = useRef(ownerDocument || getInteractionDocument())
  const [doc, setDoc] = useState(initial.current)
  useEffect(() => {
    const recover = () => {
      if (!surfaces.has(initial.current) || initial.current.defaultView?.closed) setDoc(document)
    }
    window.addEventListener('cc:surface-closed', recover)
    return () => window.removeEventListener('cc:surface-closed', recover)
  }, [])
  const content = <SurfaceProvider value={doc}><div className="cc-v2-app density-default" data-theme="light">{children}</div></SurfaceProvider>
  return doc === document ? content : createPortal(content, doc.body)
}
